import IAppLogger from '@app/lib/IAppLogger';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import { audioDevicesState } from '../audioDevicesState';
import {
  DeviceFailureResult,
  DeviceFailureRollbackFailed,
  DeviceFailureSwitchFailed,
  ErrorCategory,
  SetAudioDevicesFailedPayload,
} from '../types';
import { webPhoneEventNames } from '../constants/events';

const errorCategoryMessages: Record<ErrorCategory, string> = {
  permission_denied:
    'Microphone access was denied. Please check your browser permissions.',
  device_not_found: 'The selected audio device could not be found.',
  device_in_use: 'The selected audio device is in use by another application.',
  media_api_unavailable:
    'Audio device management is not available in this browser.',
  other: 'Failed to switch audio device. Please try again.',
};

const getErrorCategory = (
  payload: SetAudioDevicesFailedPayload,
): ErrorCategory => {
  const results: DeviceFailureResult[] = [
    payload.audioInput,
    payload.audioOutput,
    payload.ringOutput,
  ];
  const failed = results.find(
    (r): r is DeviceFailureSwitchFailed | DeviceFailureRollbackFailed =>
      r.status === 'switch_failed' || r.status === 'rollback_failed',
  );
  return failed?.errorCategory ?? 'other';
};

/**
 * Handles the `webphone:set-audio-devices-failed` event emitted by the webphone MFE
 * when a device switch fails for one or more device kinds.
 *
 * Updates active device IDs to reflect the actual devices after rollback,
 * stores the error payload, shows a passive toast, and logs error details.
 */
const handleSetAudioDevicesFailed = (
  payload: SetAudioDevicesFailedPayload,
  logger: IAppLogger,
): void => {
  const { audioInput, audioOutput, ringOutput } = payload;
  const errorCategory = getErrorCategory(payload);

  logger.error(webPhoneEventNames.setAudioDevicesFailed, {
    audioInput,
    audioOutput,
    ringOutput,
    errorCategory,
  });

  audioDevicesState.currentInputDeviceId = audioInput.currentDeviceId;
  audioDevicesState.currentOutputDeviceId = audioOutput.currentDeviceId;
  audioDevicesState.currentRingDeviceId = ringOutput.currentDeviceId;
  audioDevicesState.lastSetDevicesError = payload;

  DiagnosticsModule.reportUserError(errorCategoryMessages[errorCategory], {
    priority: 'passive',
  });
};

export default handleSetAudioDevicesFailed;
